import { IFacility } from 'es/app/business-logic/domain-models/Facility';
import { ICompany } from 'es/app/business-logic/domain-models/ICompany';
import { ILoggedInUser } from 'es/app/business-logic/domain-models/User';
import Guid from 'es/core/types/Guid';

import * as FacilityService from 'es/app/business-logic/services/facility';
import * as SecurityService from 'es/app/business-logic/services/security';
import Site from 'es/app/business-logic/domain-models/Site';

import LogManager from 'es/core/logging/LogManager';
import IAccessControlSet from 'es/app/security/IAccessControlSet';
const logger = LogManager.getLogger('core.routing.protected.authenticator');

class Authenticator {
    private companies: ICompany[] | null = null;
    private accessibleFacilities: IFacility[] | null = null;
    private selectedFacility: IFacility | null = null;
    private userDetails: ILoggedInUser | null = null;
    private accessControls = new Map<Site, Promise<IAccessControlSet>>();

    public async fetchAccessibleSites() {
        if (!this.accessibleFacilities) {
            logger.debug('Verifying the facilities the user has access to.');
            const { companies, facilities } = await FacilityService.fetchAccessibleSites();

            this.companies = companies;
            this.accessibleFacilities = facilities;
        }

        return {
            companies: this.companies,
            facilities: this.accessibleFacilities,
        };
    }

    public selectFacility(challengeFacilityId: Guid, { companyAlias, facilityAlias }: Site) {
        if (!this.accessibleFacilities?.length) {
            logger.warn('User does not have access to any facilities.');
            return (this.selectedFacility = null);
        }

        if (companyAlias && facilityAlias) {
            const siteFacility = this.accessibleFacilities.find(
                ac => ac.site.companyAlias === companyAlias && ac.site.facilityAlias === facilityAlias
            );
            if (siteFacility) {
                logger.info(`Switching to city ${siteFacility.name} via AuthDomain override`);
                return (this.selectedFacility = siteFacility);
            }
        }

        // The user may not need to select a city
        if (!challengeFacilityId) return null;

        // We've already selected the correct facility
        if (this.selectedFacility && challengeFacilityId === this.selectedFacility.id) return this.selectedFacility;

        const challengeFacility = this.accessibleFacilities.find(ac => ac.id === challengeFacilityId);
        if (challengeFacility) {
            return (this.selectedFacility = challengeFacility);
        }

        logger.warn(
            'Access to the preferred facility is forbidden for the current user; selecting first accessible city instead.'
        );
        return this.selectFirstAccessibleFacility();
    }

    private selectFirstAccessibleFacility() {
        if (!this.accessibleFacilities) return null;
        const [selectedFacility] = this.accessibleFacilities;
        if (!selectedFacility) return;
        if (selectedFacility !== this.selectedFacility) {
            logger.debug(
                `Setting preferred facility to '${selectedFacility.name}'`,
                selectedFacility.id,
                selectedFacility.name
            );
        }

        return (this.selectedFacility = selectedFacility);
    }

    public async fetchUserDetails(facility?: IFacility) {
        if (this.userDetails) return this.userDetails;

        logger.debug(`Fetching user details for site: ${facility && facility.site.key}`);

        const companyAlias = facility?.site.companyAlias;
        const facilityAlias = facility?.site.facilityAlias;

        this.userDetails = await SecurityService.fetchLoggedInUser(companyAlias, facilityAlias);

        // The user may not have authorization at the system level, so if no company/facility alias has been provided,
        // we can try again using any facility the user has access to.
        if (!this.userDetails && !(companyAlias && facilityAlias)) {
            if (!this.accessibleFacilities?.length) {
                logger.warn('Cannot get user details - user has no access for any site.');
                return null;
            }

            const [proxyFacility] = this.accessibleFacilities;
            const proxySite = proxyFacility?.site;

            if (!proxySite) return;

            logger.debug(
                `User has no access at ${
                    facility?.site?.key ?? 'system_system'
                } - attempting to retrieve user details using: ${proxySite}`
            );
            this.userDetails = await SecurityService.fetchLoggedInUser(proxySite.companyAlias, proxySite.facilityAlias);
        }

        return this.userDetails;
    }

    public fetchAccessControls(facility: IFacility | null = null): Promise<IAccessControlSet> | undefined {
        const site = (facility && facility.site) || Site.Global;

        if (this.accessControls.has(site)) {
            return this.accessControls.get(site);
        }

        logger.debug(`Validating the user's access controls for: ${site}.`);

        const companyId = facility ? facility.companyId : null;
        const facilityId = facility ? facility.id : null;

        const accessControlRequest = SecurityService.fetchAccessControls(companyId, facilityId);
        this.accessControls.set(site, accessControlRequest);

        return accessControlRequest;
    }
}

const authenticator = new Authenticator();

export default authenticator;
